/* eslint-disable no-unused-vars */
import { FormProvider, useForm } from 'react-hook-form'
import styles from '../Dialog.module.sass'
import TaskHeaderController from 'controllers/TaskManager/TaskSubControllers/TaskHeader/TaskHeader'
import { ForwardingCallHistory } from 'components/ForwardingCallHistory/ForwardingCallHistory'
import { ForwardingTaskForm } from './ForwardingTaskForm'
import { zodResolver } from '@hookform/resolvers/zod'
import { ForwardingTaskSchema } from './ForwardingTaskSchema'
import useSubmitTask, { translateErrorMessage } from 'hooks/useSubmitTask'
import FinalizeTaskDialog from '../FinalizeTaskController/FinalizeTaskDialog'
import { useGlobal } from '@jimmyboys/liita-ui/dist/components/GlobalContext'
import AlertBox from 'components/AlertBox/AlertBox'
import { ForwardingTaskContext } from 'context/ForwardingTask/ForwardingTaskContext'
import TaskMenuController from '../TaskMenu/TaskMenuController'
import { useHistory } from 'react-router-dom'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useDeepCompareEffect } from 'react-use'
import { useFetchList, useReload } from 'store'
import ProcessForwardingCallHistory from 'models/ProcessForwardingCallHistory'
import { callHistoryItems } from 'context/Drawer/drawers/Forwarding/infoItems'
import { InsuranceEntity } from '../../../../utils/enums'
import {
	API_URL,
	fetchOptions,
	parseJSONContent,
} from '../../../../store/fetchUtils'

//TODO REVER COM PORTAL
const WORKPLACE_ACCIDENT_SITE_ID = 1

const ForwardingTaskController = (props) => {
	const { i18n, expireSession } = useGlobal()
	const history = useHistory()
	const reload = useReload()
	const [defaultValues, setDefaultValues] = useState(null)
	const [trustAISuggestion, setTrustAISuggestion] = useState(null)
	const [resolverDisabled, setResolverDisabled] = useState(false)
	const [showContactForm, setShowContactForm] = useState(false)
	// Tracks how the contact form was opened: `true` when triggered by a "No
	// information" answer (justification-on-decline flow), `false` when opened
	// from the "Contact injured" menu action (graceful close on decline).
	const [contactOpenedByNoInfo, setContactOpenedByNoInfo] = useState(false)
	const validateRef = useRef(() => true)
	const { data, resolve, dismiss, id } = props
	const isAllianzInsuranceCompany =
		data?.insuranceCompany?.id === InsuranceEntity.Allianz
	const insuranceId = data?.insuranceCompany?.id

	const hasClientPhone =
		!!data?.injured?.contacts?.mobile ||
		!!data?.injured?.contacts?.telephone

	const forwardUrgencyById = {
		1: { type: 'info', title: i18n`Non urgent process forward` },
		2: { type: 'warning', title: i18n`Urgent process forward` },
		3: { type: 'error', title: i18n`Extremely urgent process forward` },
	}

	const methods = useForm({
		mode: 'onSubmit',
		resolver: zodResolver(
			ForwardingTaskSchema(
				isAllianzInsuranceCompany,
				data?.segment?.id,
				hasClientPhone
			)
		),
		shouldUnregister: true,
	})

	const { handleSubmit, formState, getValues, reset } = methods

	// The contact sub-form is hidden by default. The "Contact injured" action in
	// the options menu reveals it with "Yes" pre-selected; choosing "No" inside
	// the form hides it again.
	// Only body part and postal code share on both forms
	const revealContactForm = () => {
		setShowContactForm(true)
		setContactOpenedByNoInfo(false)
		methods.setValue('contact.contactVictim', true)
		methods.setValue('accident.isSerious', undefined)
	}
	const { error, submitting, resolveTask, setError } = useSubmitTask(
		data?.id,
		formState?.errors
	)

	const {
		contents: [callAttempts, establishments, clubs],
	} = useFetchList(
		[
			`clinical_processes/${data?.processId}/process_forward_call_attempts`,
			`metadata/valid_establishments/${
				data?.insuranceCompany?.id
			}/${encodeURIComponent(data?.admissionDate?.UTC.split('T')[0])}/${
				data?.segment?.id
			}`,
			isAllianzInsuranceCompany &&
				`metadata/insurance_company_clubs/${data.insuranceCompany.id}`,
		],
		[
			(callAttempt) => ProcessForwardingCallHistory.fromJSON(callAttempt),
			(json) => ({
				value: `${json?.idEstabelecimento}`,
				text: json?.nomeEstabelecimento,
			}),
			(json) => ({ value: json?.id, text: json?.name }),
		]
	)

	const callHistoryInfo = useMemo(
		() => callHistoryItems(callAttempts),
		[callAttempts]
	)

	useEffect(() => {
		if (
			(data.formState || defaultValues) &&
			!formState.isDirty &&
			formState.submitCount === 0
		) {
			//defaultvalues é do processo
			const initialValues =
				data.formState && defaultValues
					? {
							...defaultValues,
							...data.formState,
							dataValidation: {
								...defaultValues.dataValidation,
								...data.formState.dataValidation,
								...(defaultValues.dataValidation?.postalCode
									? {
											postalCode:
												defaultValues.dataValidation
													.postalCode,
									  }
									: {}),
							},
							contact: {
								...defaultValues.contact,
								...data.formState.contact,
							},
					  }
					: data.formState || defaultValues
			reset && reset(initialValues)
		}
	}, [
		reset,
		defaultValues,
		data.formState,
		formState.isDirty,
		formState.submitCount,
	])

	useDeepCompareEffect(() => {
		let defaultValues = {}

		// Check if process has name
		defaultValues = {
			...defaultValues,
			dataValidation: {
				...defaultValues.dataValidation,
				injuredNameValidation: !!data?.injured?.name,
			},
		}

		// Check process has accident date
		defaultValues = {
			...defaultValues,
			dataValidation: {
				...defaultValues.dataValidation,
				accidentDateValidation: !!data?.accident?.date,
			},
		}
		//Check if process has birthdate
		defaultValues = {
			...defaultValues,
			dataValidation: {
				...defaultValues.dataValidation,
				birthDateValidation: !!data?.injured?.birthday,
			},
		}

		//Check if process has nif
		if (data?.injured?.nif)
			defaultValues = {
				...defaultValues,

				dataValidation: {
					...defaultValues.dataValidation,
					nif: Number(data.injured.nif),
				},
			}
		// Check if process has email
		if (data?.injured?.contacts?.email)
			defaultValues = {
				...defaultValues,
				dataValidation: {
					...defaultValues.dataValidation,
					email: data.injured.contacts.email,
				},
			}

		//TODO VER SOFIA
		if (data?.injured?.postalCode)
			defaultValues = {
				...defaultValues,
				dataValidation: {
					...defaultValues.dataValidation,
					postalCode: data.injured.postalCode,
				},
			}

		// Assume client authorize call .
		defaultValues = {
			...defaultValues,
			contact: {
				...defaultValues.contact,
				authorizeRecording: true,
			},
		}

		setDefaultValues({ ...defaultValues })
	}, [data])

	const onSubmit = async (formData) => {
		if (!validateRef.current()) return

		const { client, dataValidation, ...remainingData } = formData

		// The client has no registered mobile — save it to the process before
		// submitting so the legacy backend can send the SMS notification.
		if (!hasClientPhone) {
			const toNumber = (value) => {
				const num = parseInt(value, 10)
				return Number.isNaN(num) ? undefined : num
			}

			const injuredBody = {
				name: data?.injured?.name,
				birthDate:
					data?.injured?.birthdayInfo?.birthdayDateMoment?.format(
						'YYYY-MM-DD'
					),
				profession: data?.injured?.professionId,
				nif: toNumber(data?.injured?.nif),
				address: data?.injured?.address,
				postalCode: data?.injured?.postalCode,
				email: data?.injured?.contacts?.email,
				telephone: toNumber(data?.injured?.contacts?.telephone),
				phoneNumber: parseInt(client?.phoneNumber, 10),
			}

			try {
				const response = await fetch(
					`${API_URL}/api/clinical_processes/${data?.processId}/injured/${data?.injured?.id}`,
					fetchOptions({ method: 'PUT', body: injuredBody })
				)
				if (!response.ok) {
					await parseJSONContent(response)
				}
			} catch (e) {
				if (e.status === '401' || e.status === 401) {
					expireSession()
				}
				setError([translateErrorMessage(e, i18n)])
				return
			}
		}

		let finalData = { ...remainingData }

		// Observations saved to the DB with injuryDescription (injury first)
		finalData.observations = [
			formData.injuryDescription,
			formData.observations,
		]
			.map((value) => (value ?? '').trim())
			.filter(Boolean)
			.join(' ')

		if (dataValidation) {
			const { email = '', ...dataValidationData } = dataValidation
			finalData = {
				...finalData,
				dataValidation: { ...dataValidationData },
			}
			if (email?.length) finalData.dataValidation['email'] = email
		}

		if (finalData.dataValidation?.injuredNameValidation === undefined) {
			finalData.dataValidation = {
				...finalData.dataValidation,
				injuredNameValidation: !!data?.injured?.name,
			}
		}

		//TODO REVER
		if (!data?.accident?.local) {
			finalData.dataValidation = {
				...finalData.dataValidation,
				accidentSiteValidation: false,
				providedAccidentSite: WORKPLACE_ACCIDENT_SITE_ID,
			}
		}

		// BloquearSMS ("Already being assisted at the selected provider"): always
		// send an explicit boolean. An untouched/unchecked checkbox leaves the
		// value undefined and it would otherwise be dropped before reaching the DB.
		finalData.provider = {
			...finalData.provider,
			alreadyBeingAssisted: !!formData.provider?.alreadyBeingAssisted,
		}

		if (trustAISuggestion) {
			try {
				const username = JSON.parse(
					localStorage.getItem('user')
				)?.username
				await fetch(`${API_URL}/api/suggest/logs`, {
					method: 'POST',
					headers: { 'Content-Type': 'application/json' },
					body: JSON.stringify({
						...trustAISuggestion,
						username,
						justification:
							trustAISuggestion.description || 'Aprovação manual',
						userJustification:
							trustAISuggestion.userJustification ||
							'Sugestao do modelo.',
						userJustificationId:
							trustAISuggestion.userJustificationId || null,
						request: JSON.stringify(trustAISuggestion),
					}),
				})
			} catch (error) {
				console.error('Erro ao guardar log:', error)
			}
		}

		resolveTask(
			{
				...finalData,
				processId: parseInt(data?.processId, 10),
				contact: {
					...formData.contact,
					successfulContact: !formData.contact?.contactVictim
						? 6 // Não aplicável
						: formData.contact.successfulContact,
				},
			},
			'submit'
		)
			.then(() => {
				reload([
					`/clinical_processes/${data?.processId}`,
					`/clinical_processes/${data?.processId}/process_forward`,
				])
				resolve()
			})
			.catch(() => {})
	}

	const customError = error?.map((err) => ({ title: i18n`Error`, text: err }))

	return (
		<>
			{submitting && (
				<FinalizeTaskDialog
					dismiss={dismiss}
					submitting={submitting}
					task={data}
				/>
			)}

			<FormProvider {...methods}>
				<div className={styles['form-container']}>
					<TaskHeaderController
						task={data}
						hideAuthor={true}
						hideSpecialtyAndAppointment={false}
					/>
					<form
						id={`form_${id}`}
						className={styles['form']}
						onSubmit={handleSubmit(onSubmit)}
					>
						{customError && customError.length > 0 && (
							<AlertBox
								className={styles['alert-box']}
								type='error'
								items={customError}
							/>
						)}

						{data.insuranceCompany?.id ===
							InsuranceEntity.Zurich && (
							<AlertBox
								className={styles['alert-box']}
								type={
									forwardUrgencyById[data.forwardUrgencyId]
										?.type
								}
								items={[
									{
										title: forwardUrgencyById[
											data.forwardUrgencyId
										]?.title,
										text: data.observations,
									},
								]}
							/>
						)}

						{callHistoryInfo.length ? (
							<ForwardingCallHistory
								callHistory={callHistoryInfo}
							/>
						) : null}

						<ForwardingTaskContext.Provider
							value={{
								injured: data?.injured,
								accident: data?.accident,
								establishments: establishments,
								isAllianzInsuranceCompany,
								clubs,
								callAttempts,
							}}
						>
							<ForwardingTaskForm
								accidentType={data?.segment?.id}
								insuranceId={insuranceId}
								processId={data?.processId}
								requisitionStateId={
									data?.requisition?.status?.id
								}
								policyNumber={
									data?.insurancePolicy?.policyNumber
								}
								hasClientPhone={hasClientPhone}
								onProviderSelected={(suggestionData) =>
									setTrustAISuggestion(suggestionData)
								}
								onResolverDisabled={setResolverDisabled}
								validateRef={validateRef}
								showContactForm={showContactForm}
								setShowContactForm={setShowContactForm}
								contactOpenedByNoInfo={contactOpenedByNoInfo}
								setContactOpenedByNoInfo={
									setContactOpenedByNoInfo
								}
							/>
						</ForwardingTaskContext.Provider>
					</form>
				</div>
			</FormProvider>

			<TaskMenuController
				data={{
					formId: id,
					options:
						data.id === 'PROCESS_PROVIDER_FORWARDING'
							? ['forward']
							: ['postpone', 'forward'],
					history,
					submitting,
					submissionType: 'submit',
					getValues,
					disableResolver: false,
					contactInjuredAction: showContactForm
						? undefined
						: revealContactForm,
				}}
			/>
		</>
	)
}

export default ForwardingTaskController
